import AddIcon from "@mui/icons-material/Add";
import { Button, Divider, Grid, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import * as React from "react";
import { useState } from "react";
import { useRecordContext } from "react-admin";
import { Controller, useForm } from "react-hook-form";
import CustomEditRemoveIcon from "../../unit/CustomEditRemoveIcon";
import CustomProductTextField from "../../unit/forms/CustomProductTextField";
import useNotify from "../../unit/useNotify";
import SectionStyled from "./SectionStyled";
import SectionTitle from "./SectionTitle";
import CustomProductActionBottom from "../CustomProductActionBottom";

export default function Authorization({
  onSave,
  defaultValues,
  ...props
}: any) {
  const itemKeyRef = React.useRef(0);
  const record = useRecordContext();
  const { success, error, warning, info } = useNotify();
  const sectionName = "authorization";
  const sectionTitle =
    "Authorization and Acknowledgement - Information that requires customer's review or approval";
  const sectionSubtitle = "";

  const defaultApplicationSubmit = [
    {
      id: "",
      title:
        "Acknowledgement and Authorization for Lender to Obtain Credit Report",
      description:
        "I understand that Spring Bank may request and use a consumer report in connection with my application for credit and subsequent consumer reports in connection with any update, renewal or extension of credit.  If I ask, Spring Bank will tell me whether or not a consumer report has been requested, and if it has been used, Spring Bank will provide me with the name and address of the consumer reporting agency that has furnished the report.",
    },
    {
      id: "",
      title:
        "Authorization for Lender to Obtain Employment and Financial Information",
      description:
        "As part of the underwriting and decision-making process for this loan, I acknowledge receipt of this notice and hereby authorize Spring Bank to verify information contained in my loan application as well as in other documents provided with my loan application, including, but not limited to, my employment history and income; my depository account balance and history; and disclosure on my real estate, automobiles and other assets.",
    },
    {
      id: "",
      title: "Consent for Receiving Automated SMS Text Messages",
      description:
        "I consent to receiving automated SMS text message alerts from Happy Mango with regard to activities on my account.  I understand that standard text charges may apply.  I also understand that I can opt out of this service at any time.",
    },
  ];

  const [applicationSubmit, setApplicationSubmit] = useState(
    (
      record?.data?.basic?.authorization?.application_submit ||
      defaultApplicationSubmit
    ).map((item: any) => ({
      ...item,
      __clientKey: `application-submit-${itemKeyRef.current++}`,
    }))
  );
  const subType = record?.data?.basic?.subType;
  const division = record?.data?.basic?.division;
  const productModulesGroup = record?.data?.basic?.productModulesGroup;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: defaultValues || {},
  });

  React.useEffect(() => {
    if (record?.data?.basic?.authorization) {
      const nextApplicationSubmit = (
        record.data.basic.authorization.application_submit ||
        defaultApplicationSubmit
      ).map((item: any) => ({
        ...item,
        __clientKey: `application-submit-${itemKeyRef.current++}`,
      }));

      setApplicationSubmit(nextApplicationSubmit);
      reset({
        applicationSubmit: nextApplicationSubmit,
      });
    }
  }, [record, reset]);
  React.useEffect(() => {
    if (props?.onDirtyChange) {
      props?.onDirtyChange(props?.curMenu, isDirty);
    }
  }, [isDirty, reset]);

  const hasEmptyApplicationSubmit = applicationSubmit.some(
    (item: any) => !item.title?.trim() || !item.description?.trim()
  );

  const buildDefaultApplicationSubmit = () =>
    defaultApplicationSubmit.map((item) => ({
      ...item,
      __clientKey: `application-submit-${itemKeyRef.current++}`,
    }));

  const sanitizeApplicationSubmit = (items: any[]) =>
    items.map(({ __clientKey, ...item }) => item);

  const persistApplicationSubmit = (nextApplicationSubmit: any[]) => {
    const formData: any = getValues();
    formData.saveAction = sectionName;
    formData.saveActionType = "applicationSubmit";
    formData.applicationSubmit = sanitizeApplicationSubmit(
      nextApplicationSubmit
    );

    if (onSave) {
      onSave(formData, props?.curMenu);
      reset({
        ...formData,
        applicationSubmit: nextApplicationSubmit,
      });
    }
  };

  const addApplicationSubmit = () => {
    const newApplicationSubmit = {
      id: "",
      title: "",
      description: "",
      __clientKey: `application-submit-${itemKeyRef.current++}`,
    };
    setApplicationSubmit((prev: any) => [...prev, newApplicationSubmit]);
    setValue(
      `applicationSubmit[${applicationSubmit.length}]`,
      newApplicationSubmit,
      {
        shouldDirty: true,
      }
    );
  };

  const addDefaultApplicationSubmit = () => {
    const nextApplicationSubmit = [
      ...applicationSubmit,
      ...buildDefaultApplicationSubmit(),
    ];
    setApplicationSubmit(nextApplicationSubmit);
    setValue("applicationSubmit", nextApplicationSubmit, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const removeAcknowledgement = (index: any) => {
    if (applicationSubmit.length > 0) {
      const updated = [...applicationSubmit];
      updated.splice(index, 1);
      setApplicationSubmit(updated);
      setValue("applicationSubmit", updated, { shouldDirty: true });
      persistApplicationSubmit(updated);
    }
    success("Acknowledgement removed and saved.");
  };

  const handleSaveClick = () => {
    if (hasEmptyApplicationSubmit) {
      warning("Title and Description cannot be empty.");
      return;
    }

    persistApplicationSubmit(applicationSubmit);
  };

  const handleApplicationSubmitChange = (
    index: any,
    field: any,
    value: any
  ) => {
    const updated = [...applicationSubmit];
    updated[index][field] = value;
    setApplicationSubmit(updated);
    setValue(`applicationSubmit[${index}].${field}`, value, {
      shouldDirty: true,
    });
  };

  return (
    <SectionStyled id={"section_" + sectionName}>
      <SectionTitle subtitle={sectionSubtitle} title={sectionTitle} />

      <Box
        sx={{
          padding: "10px",
          height: "calc(100vh - 255px)",
          overflowY: "auto",
          // background: "yellow",
        }}
      >
        {productModulesGroup?.includes("disclosure") && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Application Submission
              </Typography>
              {applicationSubmit.map((item: any, index: any) => (
                <React.Fragment key={item.__clientKey}>
                  <Grid
                    container
                    spacing={0}
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Grid item xs={10}>
                      <Controller
                        name={`applicationSubmit[${index}].title`}
                        control={control}
                        render={({ field }) => (
                          <CustomProductTextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            label="Title"
                            value={item.title}
                            onChange={(e) => {
                              field.onChange(e);
                              handleApplicationSubmitChange(
                                index,
                                "title",
                                e.target.value
                              );
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        type="hidden"
                        name={`applicationSubmit[${index}].id`}
                        value={item.id}
                        sx={{ display: "none" }}
                      />
                      {applicationSubmit.length > 0 && (
                        <CustomEditRemoveIcon
                          onClick={() => removeAcknowledgement(index)}
                          tooltip="Remove this acknowledgement"
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name={`applicationSubmit[${index}].description`}
                        control={control}
                        render={({ field }) => (
                          <CustomProductTextField
                            {...field}
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            label="Description"
                            value={item.description}
                            onChange={(e) => {
                              field.onChange(e);
                              handleApplicationSubmitChange(
                                index,
                                "description",
                                e.target.value
                              );
                            }}
                            sx={{ marginTop: "5px" }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  {index < applicationSubmit.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </React.Fragment>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addApplicationSubmit}
                sx={{ width: "120px" }}
              >
                Add
              </Button>
              <Button
                variant="outlined"
                onClick={addDefaultApplicationSubmit}
                sx={{ width: "140px", ml: 2 }}
              >
                Use Default
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
      {/* Form Actions */}
      <CustomProductActionBottom>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          disabled={!isDirty || hasEmptyApplicationSubmit}
          sx={{ width: "120px", mt: 1 }}
        >
          Save
        </Button>
      </CustomProductActionBottom>
    </SectionStyled>
  );
}
